import { yupResolver } from '@hookform/resolvers/yup';
import { IconHexagons, IconSwitchHorizontal } from '@tabler/icons-react';
import { Role } from '@zorro/clients';
import { KeyedError, VALIDATION_MESSAGES } from '@zorro/common-utils';
import { useDrawerFooter } from '@zorro/design-system/v2/Drawer';
import { RadioCard, RadioGroup } from '@zorro/design-system/v2/Radio';
import { Group, Icon, Stack } from '@zorro/web/design-system';
import {
  showSuccessNotification,
  showWarningNotification,
  useForm,
  useMonolithMutation,
} from '@zorro/web/ui-utils';
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { useAnalytics } from '../../Analytics';
import { FormFooter } from '../../FormFooter';
import { RecordForActions } from '../EmployeeActionsUtils';
import { ErrorsTable } from '../Errors/ErrorsTable';

// 🔮 tech debt: should be fixed at the `EmployeeRow` definition
function getEmployeeCurrentRole(role?: string): Role {
  if (!role || role === Role.EMPLOYEE) {
    return Role.EMPLOYEE;
  }
  return Role.EMPLOYER_ADMIN;
}

function getEmployeesCurrentRole(recordsForActions: RecordForActions[]) {
  const roles = recordsForActions
    .filter((record) => record.role !== undefined)
    .map((record) => record.role);
  const uniqueRoles = [...new Set(roles)]; // Get unique roles

  if (uniqueRoles.length === 1) {
    // If there's only one unique role, return that role
    return getEmployeeCurrentRole(uniqueRoles[0]);
  }

  // If there are more than one unique role (i.e. some admins and some employees)
  return undefined;
}

const editEmployeeRoleSchema = yup.object({
  role: yup
    .mixed<Role>()
    .required(VALIDATION_MESSAGES.roleRequired)
    .oneOf(Object.values(Role), VALIDATION_MESSAGES.roleRequired),
});

type EditEmployeeRoleFormFields = yup.InferType<typeof editEmployeeRoleSchema>;

type EditEmployeeRoleFormProps = {
  recordsForActions: RecordForActions[];
  onSuccess: () => void;
};

export const EditEmployeeRoleForm = ({
  recordsForActions,
  onSuccess,
}: EditEmployeeRoleFormProps) => {
  const isSingleEmployee = recordsForActions.length === 1;
  const singleEmployee = recordsForActions[0];
  const { sendEvent } = useAnalytics();

  const [errors, setErrors] = useState<KeyedError[]>([]);

  // `tryMutate` because the endpoint rejects outright on an unresolvable
  // user id, on top of the per-user outcomes it reports on success.
  const { tryMutate: changeRole } = useMonolithMutation({
    method: 'usersControllerChangeCustomerFacingRole',
  });

  const { control, getValues, formState } = useForm<EditEmployeeRoleFormFields>(
    {
      mode: 'all',
      resolver: yupResolver(editEmployeeRoleSchema),
      defaultValues: {
        role: isSingleEmployee
          ? getEmployeeCurrentRole(singleEmployee.role)
          : getEmployeesCurrentRole(recordsForActions),
      },
    }
  );

  const { isValid } = formState;

  const handleSubmit = async () => {
    const { role } = getValues();
    void sendEvent('click_save_role', {
      from_role: singleEmployee.role,
      to_role: role,
    });

    const result = await changeRole([
      { userIds: recordsForActions.map((record) => record.userId), role },
    ]);

    // The hook already showed the rejection as an error notification.
    if (result.isErr()) {
      return;
    }
    const { failed } = result.value;

    const nameByUserId = new Map(
      recordsForActions.map((record) => [record.userId, record.fullName])
    );
    setErrors(
      failed.map(({ userId, message }) => ({
        key: nameByUserId.get(userId) ?? userId,
        error: message,
      }))
    );

    if (failed.length === 0) {
      showSuccessNotification({ message: 'Role updated' });
      onSuccess();
      return;
    }

    showWarningNotification({
      message: `Role updated for ${
        recordsForActions.length - failed.length
      } of ${recordsForActions.length} employees.`,
    });
  };

  const drawerFooter = useMemo(
    () => (
      <FormFooter
        withPaper={false}
        primaryLabel="Save"
        primaryButtonProps={{
          onClick: handleSubmit,
          disabled: !isValid,
        }}
      />
    ),
    [handleSubmit, isValid]
  );
  useDrawerFooter(drawerFooter);

  return (
    <Stack>
      <Controller
        control={control}
        name="role"
        render={({ field }) => (
          <RadioGroup {...field} description="Role">
            <Group wrap="nowrap">
              <RadioCard
                icon={<Icon icon={IconHexagons} />}
                iconColor="yellow"
                label="Employee"
                value={Role.EMPLOYEE}
              />
              <RadioCard
                icon={<Icon icon={IconSwitchHorizontal} />}
                iconColor="mint"
                label="Admin"
                value={Role.EMPLOYER_ADMIN}
              />
            </Group>
          </RadioGroup>
        )}
      />

      <ErrorsTable
        tableTitle="Employee name"
        errors={errors}
        isBulk={recordsForActions.length > 1}
      />
    </Stack>
  );
};
